<script setup lang="ts" generic="T = boolean">
  import { reactiveOmit } from '@vueuse/core';
  import type { CheckboxRootProps } from 'reka-ui';
  import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'reka-ui';
  import type { HTMLAttributes } from 'vue';

  import { cn } from '@ui/utils/utils';

  // Props mirror reka-ui's generic `CheckboxRoot<T>` (default boolean). The emit, however, is
  // narrowed to plain `T`: reka's own `CheckboxRootEmits<T>` always widens the payload to
  // `T | 'indeterminate'`, which would force every consumer of the common boolean checkbox to
  // handle an 'indeterminate' case they never use. Consumers that need tri-state opt in by
  // instantiating `<Checkbox<boolean | 'indeterminate'>>`.
  const props = defineProps<CheckboxRootProps<T> & { class?: HTMLAttributes['class'] }>();
  const emits = defineEmits<{ 'update:modelValue': [value: T] }>();

  const delegatedProps = reactiveOmit(props, 'class');

  const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <!-- @vue-ignore reka's CheckboxRoot expects an `update:modelValue` handler taking
       `T | 'indeterminate'`; our wrapper narrows the public emit to plain `T` (boolean
       checkboxes don't surface 'indeterminate'). The forwarded binding is sound at runtime —
       this only suppresses that intentional narrowing at the internal boundary. -->
  <CheckboxRoot
    v-slot="slotProps"
    data-slot="checkbox"
    v-bind="forwarded"
    :class="
      cn(
        'peer border-muted-foreground/40 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-3.5 shrink-0 rounded-sm border outline-none focus-visible:ring-[2px] disabled:cursor-not-allowed disabled:opacity-50',
        props.class,
      )
    "
  >
    <CheckboxIndicator
      data-slot="checkbox-indicator"
      class="grid place-content-center transition-none"
    >
      <slot v-bind="slotProps">
        <i class="fa fa-check text-2xs" />
      </slot>
    </CheckboxIndicator>
  </CheckboxRoot>
</template>
